import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatCurrency } from '../utils/formatUtils';
import { 
  Users, 
  Plus, 
  Filter, 
  Search, 
  Edit3,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  UserPlus,
  ChevronDown,
  Check
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom Input-styled Dropdown Component
const InputDropdown = ({ value, onChange, options, placeholder, className, testId, allowInput = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const filteredOptions = allowInput 
    ? options.filter(option => option.toLowerCase().includes(inputValue.toLowerCase()))
    : options;

  const handleSelect = (option) => {
    setInputValue(option);
    onChange(option);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (allowInput) {
      onChange(newValue);
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        placeholder={placeholder}
        className={`form-input ${className || ''}`}
        data-testid={testId}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              onClick={() => handleSelect(option)}
              className="px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-gray-100"
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom Select in Input Style Component (for non-editable dropdowns)
const InputSelect = ({ value, onChange, options, placeholder, className, testId }) => {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`form-input appearance-none cursor-pointer ${className || ''}`}
      data-testid={testId}
    >
      <option value="">{placeholder}</option>
      {options.map((option, index) => (
        <option key={index} value={option.value || option}>
          {option.label || option}
        </option>
      ))}
    </select>
  );
};

// MultiSelect Component for multiple selection filters
const MultiSelect = ({ value, onChange, options, placeholder, className, testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedValues = Array.isArray(value) ? value : [];

  const handleToggle = (optionValue) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newValues);
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const option = options.find(opt => (opt.value || opt) === selectedValues[0]);
      return option ? (option.label || option) : selectedValues[0];
    }
    return `Выбрано: ${selectedValues.length}`;
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`form-input text-left flex justify-between items-center ${className || ''}`}
        data-testid={testId}
      >
        <span className={selectedValues.length === 0 ? 'text-gray-500' : ''}>
          {getDisplayText()}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {options.map((option, index) => {
              const optionValue = option.value || option;
              const optionLabel = option.label || option;
              const isSelected = selectedValues.includes(optionValue);
              
              return (
                <div
                  key={index}
                  onClick={() => handleToggle(optionValue)}
                  className={`px-3 py-2 text-sm cursor-pointer flex items-center hover:bg-gray-100 ${
                    isSelected ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                  }`}
                >
                  <div className={`w-4 h-4 mr-2 border rounded flex-shrink-0 ${
                    isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                  }`}>
                    {isSelected && (
                      <Check className="w-3 h-3 text-white m-0.5" />
                    )}
                  </div>
                  {optionLabel}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const ClientsPage = () => {
  const { user } = useContext(AuthContext);
  const { t, tt } = useLanguage();
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  // Загружаем фильтры из localStorage при инициализации
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('clientsPageFilters');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Миграция: если affiliate содержит числовые ID, очищаем
        if (parsed.affiliate && Array.isArray(parsed.affiliate) && parsed.affiliate.length > 0) {
          // Проверяем если есть числовые ID (старый формат)
          if (parsed.affiliate.some(id => typeof id === 'number')) {
            console.log('⚠️ Очищаем старые числовые ID affiliate из localStorage');
            parsed.affiliate = [];
          }
        }
        return parsed;
      } catch (e) {
        console.error('Error parsing saved filters:', e);
      }
    }
    return {
      status: [],
      assigned_manager: [],
      balance_min: '',
      balance_max: '',
      campaign: [],
      affiliate: [],
      source: '',
      country: [],
      last_comment_period: '',
      last_comment_from: '',
      last_comment_to: '',
      created_date_from: '',
      created_date_to: ''
    };
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchId, setSearchId] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Сохраняем фильтры в localStorage при изменении
  useEffect(() => {
    localStorage.setItem('clientsPageFilters', JSON.stringify(filters));
  }, [filters]);
  const [managers, setManagers] = useState([]);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    campaign: '',
    status: 'new'
  });
  const [errors, setErrors] = useState({});
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignManager, setBulkAssignManager] = useState('');
  const [bulkAssignStatus, setBulkAssignStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalClients, setTotalClients] = useState(0); // For pagination from backend
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');
  const [columnOrder, setColumnOrder] = useState([
    'name', 'email', 'phone', 'country', 'balance', 'ftd', 'status', 'campaign', 'manager', 'last_comment', 'created_at'
  ]);
  const [countries, setCountries] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [unprocessedLeadIds, setUnprocessedLeadIds] = useState(new Set());

  const statuses = [
    { value: 'new', label: tt('Новый'), color: 'blue' },
    { value: 'ftd', label: tt('ФТД'), color: 'green' },
    { value: 'in_progress', label: tt('В работе'), color: 'yellow' },
    { value: 'potential', label: tt('Потенциальный'), color: 'purple' },
    { value: 'not_potential', label: tt('Не потенциальный'), color: 'red' },
    { value: 'under_18', label: tt('Нет 18'), color: 'red' },
    { value: 'no_russian', label: tt('Не говорит на рус'), color: 'pink' },
    { value: 'not_interested', label: tt('Не интересно'), color: 'gray' },
    { value: 'no_answer', label: tt('Не отвечает'), color: 'orange' },
    { value: 'replace', label: tt('Заменить'), color: 'yellow' },
    { value: 'duplicate', label: tt('Дубль'), color: 'orange' },
    { value: 'fake', label: tt('Фейк'), color: 'red' }
  ];

  const commentPeriods = [
    { value: 'today', label: tt('Сегодня') },
    { value: 'yesterday', label: tt('Вчера') },
    { value: 'week', label: tt('За неделю') },
    { value: 'month', label: tt('За месяц') },
    { value: 'no_comments', label: tt('Без комментариев') }
  ];

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    fetchClients();
    // DON'T fetch filter options on every filter change - only on mount
  }, [filters]);

  // Fetch clients when page changes
  useEffect(() => {
    fetchClients();
  }, [currentPage, itemsPerPage]);

  // Load filter options ONCE on mount
  useEffect(() => {
    fetchFilterOptions();
    fetchAffiliates();
    fetchManagers();
    fetchUnprocessedLeads();
  }, []);

  const fetchUnprocessedLeads = async () => {
    const allowedRoles = ['superadmin', 'team_lead_sales', 'sales_manager'];
    if (!user || !allowedRoles.includes(user.role)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/unprocessed-affiliate-leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ids = new Set((res.data.leads || []).map(l => l.id));
      setUnprocessedLeadIds(ids);
    } catch (e) {
      // Silently fail
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/filter-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCampaigns(response.data.campaigns || []);
      setCountries(response.data.countries || []);
      // НЕ устанавливаем affiliates здесь — это делает fetchAffiliates() с правильным форматом {id, name}
      
      console.log(`✅ Filter options loaded: ${response.data.total_campaigns} campaigns, ${response.data.total_countries} countries`);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Add pagination parameters
      params.append('skip', ((currentPage - 1) * itemsPerPage).toString());
      params.append('limit', itemsPerPage.toString());
      
      // Backend filters - send ALL to backend for fast filtering
      if (Array.isArray(filters.status) && filters.status.length > 0) {
        // Send ALL selected statuses to backend
        filters.status.forEach(s => params.append('status', s));
      } else if (typeof filters.status === 'string' && filters.status) {
        params.append('status', filters.status);
      }
      
      // Campaign filter
      if (Array.isArray(filters.campaign) && filters.campaign.length > 0) {
        filters.campaign.forEach(c => params.append('campaign', c));
      }
      
      // Country filter
      if (Array.isArray(filters.country) && filters.country.length > 0) {
        filters.country.forEach(c => params.append('country', c));
      }
      
      // Affiliate filter
      if (Array.isArray(filters.affiliate) && filters.affiliate.length > 0) {
        filters.affiliate.forEach(a => params.append('affiliate_id', a));
      }
      
      // Manager filter - send ALL selected managers to backend
      if (Array.isArray(filters.assigned_manager) && filters.assigned_manager.length > 0) {
        filters.assigned_manager.forEach(m => params.append('assigned_manager', m));
      }
      
      // Balance filters
      if (filters.balance_min) params.append('balance_min', filters.balance_min);
      if (filters.balance_max) params.append('balance_max', filters.balance_max);
      
      if (searchTerm) {
        params.append('search', searchTerm);
        // Если введено число — также ищем по ID
        if (/^\d+$/.test(searchTerm.trim())) {
          params.append('search_id', searchTerm.trim());
        }
      }
      if (filters.created_date_from) params.append('created_date_from', filters.created_date_from);
      if (filters.created_date_to) params.append('created_date_to', filters.created_date_to);
      if (filters.last_comment_period) params.append('last_comment_period', filters.last_comment_period);
      
      const response = await axios.get(`${API}/clients?${params}`);
      
      // Backend now returns: {items, total, skip, limit, has_more}
      const { items: clientsData, total } = response.data;
      
      console.log(`📥 Получено ${clientsData.length} клиентов (страница ${currentPage}, всего: ${total})`);
      
      setTotalClients(total);
      
      // Minimal client-side filtering - only for multi-select status/manager
      // Most filters are now handled on backend
      let filteredClients = clientsData;
      
      const hasClientFilters = filters.source ||
                        (Array.isArray(filters.status) && filters.status.length > 1) ||
                        (Array.isArray(filters.assigned_manager) && filters.assigned_manager.length > 1);
      
      if (hasClientFilters) {
        filteredClients = clientsData.filter(client => {
          let matches = true;
          
          // Only multi-select status (backend handles single)
          if (Array.isArray(filters.status) && filters.status.length > 1) {
            matches = matches && filters.status.includes(client.status);
          }
          
          // Only multi-select manager (backend handles single)
          if (Array.isArray(filters.assigned_manager) && filters.assigned_manager.length > 1) {
            const hasNullFilter = filters.assigned_manager.includes('null');
            const selectedManagerIds = filters.assigned_manager.filter(id => id !== 'null').map(id => String(id));
            const clientManagerId = client.assigned_manager ? String(client.assigned_manager) : null;
            
            let isMatch = false;
            if (hasNullFilter && (clientManagerId === null || clientManagerId === 'null')) isMatch = true;
            if (clientManagerId && selectedManagerIds.includes(clientManagerId)) isMatch = true;
            
            matches = matches && isMatch;
          }
          
          if (Array.isArray(filters.affiliate) && filters.affiliate.length > 0) {
            const selectedAffiliateNames = filters.affiliate.map(id => {
              const affiliate = affiliates.find(a => a.id === id);
              return affiliate ? affiliate.name : id;
            }).filter(Boolean);
            
            if (client.campaign) {
              let clientAffiliateName = client.campaign.includes(' - ')
                ? client.campaign.split(' - ').pop().trim()
                : client.campaign.trim();
              
              matches = matches && selectedAffiliateNames.some(name => 
                name.toLowerCase() === clientAffiliateName.toLowerCase()
              );
            } else {
              matches = false;
            }
          }
          
          if (filters.source && !client.source?.toLowerCase().includes(filters.source.toLowerCase())) matches = false;
          
          if (filters.last_comment_period) {
            const now = new Date();
            const clientCommentDate = client.last_comment_date ? new Date(client.last_comment_date) : null;
            
            switch (filters.last_comment_period) {
              case 'today':
                matches = matches && clientCommentDate && clientCommentDate.toDateString() === now.toDateString();
                break;
              case 'yesterday':
                const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
                matches = matches && clientCommentDate && clientCommentDate.toDateString() === yesterday.toDateString();
                break;
              case 'week':
                const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);
                matches = matches && clientCommentDate && clientCommentDate >= weekAgo;
                break;
              case 'month':
                const monthAgo = new Date(now); monthAgo.setMonth(monthAgo.getMonth() - 1);
                matches = matches && clientCommentDate && clientCommentDate >= monthAgo;
                break;
              case 'no_comments':
                matches = matches && !clientCommentDate;
                break;
            }
          }
          
          return matches;
        });
      }
      
      setClients(filteredClients);
      
      // Save filtered clients list to sessionStorage for navigation in ClientCard
      sessionStorage.setItem('clientsList', JSON.stringify(filteredClients));
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      setTotalClients(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setManagers(response.data);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  // Function to get manager name - use assigned_manager_name from API response
  const getManagerName = (client) => {
    // Handle null/undefined client
    if (!client || client === null || client === undefined) return '-';
    
    // If client is passed as object with assigned_manager_name, use it
    if (typeof client === 'object' && client.assigned_manager_name) {
      return client.assigned_manager_name;
    }
    // If client is passed as managerId (backward compatibility)
    const managerId = typeof client === 'object' ? client.assigned_manager : client;
    if (!managerId || managerId === null) return '-';
    const manager = managers.find(m => m.id == managerId); // Use == for flexible comparison
    return manager ? manager.name : '-';  // Return '-' instead of showing ID for deleted managers
  };

  const fetchAffiliates = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // OPTIMIZED: Use filter-options endpoint instead of loading 10000 clients
      // This endpoint already has affiliates from backend aggregation
      const filterOptionsResponse = await axios.get(`${API}/clients/filter-options`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const affiliatesFromBackend = filterOptionsResponse.data.affiliates || [];
      
      // Получаем токены аффилейтов (если есть доступ)
      let activeTokens = [];
      try {
        const tokensResponse = await axios.get(`${API}/affiliate-tokens`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        activeTokens = tokensResponse.data;
      } catch (err) {
        console.log('No access to affiliate tokens or none exist');
      }
      
      // Объединяем affiliates из backend и активные токены
      const uniqueAffiliateNames = new Set();
      
      // Добавляем активные токены
      activeTokens.forEach(tokenItem => {
        uniqueAffiliateNames.add(tokenItem.name);
      });
      
      // Добавляем affiliates из backend (уже содержат имена из campaign)
      affiliatesFromBackend.forEach(affiliate => {
        if (typeof affiliate === 'string') {
          uniqueAffiliateNames.add(affiliate);
        } else if (affiliate.name) {
          uniqueAffiliateNames.add(affiliate.name);
        }
      });
      
      // Преобразуем в массив объектов и сортируем
      const affiliatesList = Array.from(uniqueAffiliateNames)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({
          id: name,
          name: name
        }));
      
      console.log(`✅ Загружено ${affiliatesList.length} уникальных affiliate (из filter-options)`);
      setAffiliates(affiliatesList);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      // Не показываем ошибку пользователю, так как не все роли имеют доступ к аффилейтам
    }
  };

  // Removed old search functions - replaced by unified search below

  const clearFilters = () => {
    setFilters({
      status: [],
      assigned_manager: [],
      balance_min: '',
      balance_max: '',
      campaign: [],
      affiliate: [],
      source: '',
      country: [],
      last_comment_period: '',
      last_comment_from: '',
      last_comment_to: '',
      created_date_from: '',
      created_date_to: ''
    });
    setSearchTerm('');
    setSearchId('');
  };

  const handleSearch = async () => {
    const trimmedTerm = searchTerm.trim();
    
    if (!trimmedTerm) {
      setCurrentPage(1);
      fetchClients();
      return;
    }
    
    // Сбрасываем на первую страницу при поиске
    setCurrentPage(1);
    
    // Всегда делаем серверный поиск — backend сам ищет по ID, имени, телефону, email
    fetchClients();
  };

  const handleSearchById = async () => {
    // Теперь объединён с handleSearch
    handleSearch();
  };

  // Bulk selection functions
  const handleSelectClient = (clientId) => {
    console.log('👥 ClientsPage: Selecting client', { clientId, currentSelected: Array.from(selectedClients) });
    
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
      console.log('➖ ClientsPage: Deselected client', clientId);
    } else {
      newSelected.add(clientId);
      console.log('➕ ClientsPage: Selected client', clientId);
    }
    
    console.log('👥 ClientsPage: New selection', Array.from(newSelected));
    setSelectedClients(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedClients.size === paginatedClients.length && 
        paginatedClients.every(client => selectedClients.has(client.id))) {
      // Если все клиенты на странице выбраны, снимаем выбор
      const newSelection = new Set(selectedClients);
      paginatedClients.forEach(client => newSelection.delete(client.id));
      setSelectedClients(newSelection);
    } else {
      // Выбираем всех клиентов на текущей странице
      const newSelection = new Set(selectedClients);
      paginatedClients.forEach(client => newSelection.add(client.id));
      setSelectedClients(newSelection);
    }
  };

  const handleBulkAssignManager = async () => {
    if (selectedClients.size === 0) return;

    try {
      const selectedClientIds = Array.from(selectedClients);
      
      // Получаем данные выбранных клиентов перед назначением
      const selectedClientData = clients.filter(client => 
        selectedClientIds.includes(client.id)
      );
      
      await axios.post(`${API}/clients/bulk-assign-manager`, {
        client_ids: selectedClientIds,
        assigned_manager: bulkAssignManager || null,
        ...(bulkAssignStatus && bulkAssignStatus !== '' ? { status: bulkAssignStatus } : {})
      });
      
      // Отправляем уведомления назначенному менеджеру
      if (bulkAssignManager && window.NotificationPanel) {
        selectedClientData.forEach(client => {
          // Создаем уведомление о назначении клиента
          window.NotificationPanel.addClientAssignedNotification(
            client.name, 
            client.id
          );
        });
      }
      
      setShowBulkAssignModal(false);
      setSelectedClients(new Set());
      setBulkAssignManager('');
      setBulkAssignStatus('');
      fetchClients(); // Refresh data
    } catch (error) {
      console.error('Error bulk assigning manager:', error);
      setErrors({ bulk: error.response?.data?.detail || 'Ошибка при назначении менеджера' });
    }
  };

  // [COMMENTS-DELETE-2026-07-17] Superadmin-only: delete ALL comments across
  // the currently selected clients. Requires typed confirmation to avoid
  // accidental purges. Other clients' comments are never touched.
  const handleBulkDeleteComments = async () => {
    if (selectedClients.size === 0 || user?.role !== 'superadmin') return;
    const count = selectedClients.size;
    const first = window.confirm(
      tt(`Удалить ВСЕ комментарии у ${count} выбранных клиентов? Это действие нельзя отменить.`)
    );
    if (!first) return;
    try {
      const selectedClientIds = Array.from(selectedClients);
      const res = await axios.post(`${API}/clients/bulk-delete-comments`, {
        client_ids: selectedClientIds,
      });
      const deleted = res.data?.deleted ?? 0;
      alert(tt(`Удалено комментариев: ${deleted}`));
      setSelectedClients(new Set());
      fetchClients();
    } catch (error) {
      console.error('Error bulk deleting comments:', error);
      alert(tt('Не удалось удалить комментарии') + ': ' + (error?.response?.data?.detail || error.message || ''));
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    const newErrors = {};
    if (!newClient.name.trim()) newErrors.name = 'Обязательное поле';
    if (!newClient.email.trim()) newErrors.email = 'Обязательное поле';
    if (!newClient.phone.trim()) newErrors.phone = 'Обязательное поле';
    if (!newClient.country.trim()) newErrors.country = 'Обязательное поле';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await axios.post(`${API}/clients`, newClient);
      setShowAddModal(false);
      setNewClient({ name: '', email: '', phone: '', country: '', campaign: '' });
      setErrors({});
      fetchClients();
    } catch (error) {
      console.error('Error creating client:', error);
      
      if (error.response?.data?.detail) {
        // Handle Pydantic validation errors which can be array of objects
        let errorMessage;
        if (Array.isArray(error.response.data.detail)) {
          // Extract messages from Pydantic validation error objects
          errorMessage = error.response.data.detail
            .map(err => err.msg || err.message || JSON.stringify(err))
            .join('. ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
        setErrors({ general: errorMessage });
      } else {
        setErrors({ general: 'Ошибка при создании клиента' });
      }
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const handleColumnReorder = (fromIndex, toIndex) => {
    const newOrder = [...columnOrder];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setColumnOrder(newOrder);
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/clients/export/excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        // Get filename from response headers or generate default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'clients_export.xlsx';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/"/g, '');
          }
        }
        
        // Download the file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Export failed:', response.statusText);
        alert(tt('Ошибка экспорта данных. Попробуйте позже.'));
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(tt('Ошибка экспорта данных. Проверьте подключение к сети.'));
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = statuses.find(s => s.value === status) || { label: status, color: 'gray' };
    return (
      <span className={`status-badge status-${status.replace('_', '-')}`}>
        {statusInfo.label}
      </span>
    );
  };

  // Sort clients locally
  const sortedClients = [...clients].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // Handle special cases
    if (sortField === 'balance') {
      aValue = parseFloat(a.total_deposits) || 0;
      bValue = parseFloat(b.total_deposits) || 0;
    } else if (sortField === 'name' || sortField === 'email' || sortField === 'phone' || sortField === 'country' || sortField === 'campaign') {
      aValue = (aValue || '').toLowerCase();
      bValue = (bValue || '').toLowerCase();
    } else if (sortField === 'created_at' || sortField === 'last_comment_date') {
      // Handle date sorting - null dates go to the end
      if (!aValue && !bValue) return 0;
      if (!aValue) return sortDirection === 'asc' ? 1 : -1;
      if (!bValue) return sortDirection === 'asc' ? -1 : 1;
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    } else if (sortField === 'id') {
      aValue = parseInt(aValue) || 0;
      bValue = parseInt(bValue) || 0;
    } else if (sortField === 'ftd') {
      aValue = a.ftd ? 1 : 0;
      bValue = b.ftd ? 1 : 0;
    } else if (sortField === 'status') {
      aValue = (aValue || '').toLowerCase();
      bValue = (bValue || '').toLowerCase();
    }
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
    if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? -1 : 1;
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });

  // Filter clients based on created date filters
  const filteredClients = sortedClients.filter(client => {
    // Filter out null/undefined clients
    if (!client) return false;
    
    // Filter by status
    if (filters.status.length > 0 && !filters.status.includes(client.status)) {
      return false;
    }
    
    // Filter by assigned manager - ИСПРАВЛЕНО: учитываем 'null' и приводим типы
    if (filters.assigned_manager.length > 0) {
      const hasNullFilter = filters.assigned_manager.includes('null');
      const selectedManagerIds = filters.assigned_manager
        .filter(id => id !== 'null')
        .map(m => String(m));
      
      const clientManager = client.assigned_manager ? String(client.assigned_manager) : null;
      
      let isMatch = false;
      if (hasNullFilter && !clientManager) {
        isMatch = true;
      }
      if (clientManager && selectedManagerIds.includes(clientManager)) {
        isMatch = true;
      }
      
      if (!isMatch) return false;
    }
    
    // Filter by affiliate — [AFF-2026-05-13] check both new `affiliate_token_name`
    // field (for new leads) AND legacy concatenated `campaign` (for old leads).
    if (filters.affiliate.length > 0) {
      const tokenName = client.affiliate_token_name || '';
      const camp = client.campaign || '';
      const hasMatchingAffiliate = filters.affiliate.some(aff =>
        tokenName === aff || camp.includes(aff)
      );
      if (!hasMatchingAffiliate) return false;
    }
    
    // Filter by campaign - ИСПРАВЛЕНО: точное совпадение после trim
    if (filters.campaign.length > 0) {
      const clientCampaign = (client.campaign || '').trim();
      const hasMatch = filters.campaign.some(filterCamp => 
        filterCamp.trim() === clientCampaign
      );
      if (!hasMatch) return false;
    }
    
    // Filter by source
    if (filters.source && client.source !== filters.source) {
      return false;
    }
    
    // Filter by country
    if (filters.country.length > 0 && !filters.country.includes(client.country)) {
      return false;
    }
    
    // Filter by created date range
    if (filters.created_date_from || filters.created_date_to) {
      const clientDate = new Date(client.created_at || client.registration_date);
      
      if (filters.created_date_from) {
        const fromDate = new Date(filters.created_date_from);
        if (clientDate < fromDate) return false;
      }
      
      if (filters.created_date_to) {
        const toDate = new Date(filters.created_date_to);
        toDate.setDate(toDate.getDate() + 1); // Include end date
        if (clientDate >= toDate) return false;
      }
    }
    
    return true;
  });

  // Используем formatDateTime из TimezoneContext

  const getStatusColor = (status) => {
    const statusItem = statuses.find(s => s.value === status);
    return statusItem?.color || 'gray';
  };

  const getStatusLabel = (status) => {
    const statusItem = statuses.find(s => s.value === status);
    return statusItem?.label || status;
  };

  // Pagination logic - use totalClients from backend
  const totalPages = Math.ceil(totalClients / itemsPerPage);
  // Backend already handles pagination, no need to slice on frontend
  const paginatedClients = filteredClients;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedClients(new Set()); // Clear selections when changing page
    }
  };

  return (
    <div className="page-container">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">{t('cl_title')}</h1>
            <p className="page-subtitle">{t('cl_title')}</p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            {/* Export button - only for superadmin */}
            {user?.role === 'superadmin' && (
              <button
                onClick={handleExportExcel}
                className="btn btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-2"
                data-testid="export-clients-button"
                title={tt('Экспорт всех клиентов в Excel')}
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 16.5L12 21l9-4.5M3 12L12 16.5L21 12M3 7.5L12 12L21 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Excel
              </button>
            )}
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary text-xs sm:text-sm px-2 sm:px-3 py-2"
              data-testid="add-client-button"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              {tt('Добавить')}
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="card p-6 mb-6 [&_.form-input]:!mb-0 [&_.form-select]:!mb-0">
          {/* Unified Search Row */}
          <div className="flex gap-3 mb-4 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('search')}</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={tt('ID, имя, email, телефон...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="form-input pl-8 h-[32px] text-sm w-full"
                  data-testid="search-clients"
                />
              </div>
            </div>
            <button
              onClick={handleSearch}
              className="btn btn-primary h-[32px] px-4 text-sm whitespace-nowrap flex-shrink-0"
              data-testid="search-button"
            >
              <Search className="w-4 h-4 mr-2" />
              {tt('Поиск')}
            </button>
          </div>

          {/* Filters Row - Compact Layout */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 items-end">
            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('cl_status')}</label>
              <MultiSelect
                value={filters.status}
                onChange={(value) => setFilters({...filters, status: value})}
                options={statuses}
                placeholder={tt('Все')}
                className="h-8 text-sm"
                testId="filter-status"
              />
            </div>

            {/* Manager Filter - Only for superadmin */}
            {(user?.role === 'superadmin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('cl_manager')}</label>
                <MultiSelect
                  value={filters.assigned_manager}
                  onChange={(value) => setFilters({...filters, assigned_manager: value})}
                  options={[
                    { value: 'null', label: tt('Не назначен') },
                    ...managers.map(manager => ({ value: manager.id, label: manager.name }))
                  ]}
                  placeholder={tt('Все')}
                  className="h-8 text-sm"
                  testId="filter-manager"
                />
              </div>
            )}

            {/* Campaign Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('cl_campaign')}</label>
              <MultiSelect
                value={filters.campaign}
                onChange={(value) => setFilters({...filters, campaign: value})}
                options={campaigns.map(campaign => ({ value: campaign, label: campaign }))}
                placeholder={tt('Все')}
                className="h-8 text-sm"
                testId="filter-campaign"
              />
            </div>

            {/* Affiliate Filter - Only for admin and superadmin */}
            {(user?.role === 'superadmin' || user?.role === 'admin') && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('int_api_tokens')}</label>
                <MultiSelect
                  value={filters.affiliate}
                  onChange={(value) => setFilters({...filters, affiliate: value})}
                  options={affiliates.map(affiliate => ({ value: affiliate.id, label: affiliate.name }))}
                  placeholder={tt('Все')}
                  className="h-8 text-sm"
                  testId="filter-affiliate"
                />
              </div>
            )}

            {/* Country Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('cl_country')}</label>
              <MultiSelect
                value={filters.country}
                onChange={(value) => setFilters({...filters, country: value})}
                options={countries.map(country => ({ value: country, label: country }))}
                placeholder={tt('Все')}
                className="h-8 text-sm"
                testId="filter-country"
              />
            </div>

            {/* Last Comment Period Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('cl_comments')}</label>
              <InputSelect
                value={filters.last_comment_period}
                onChange={(value) => setFilters({...filters, last_comment_period: value})}
                options={commentPeriods}
                placeholder={tt('Все')}
                className="h-8 text-sm"
                testId="filter-comment-period"
              />
            </div>

            {/* Balance Min Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('cl_balance')} {t('cl_today').slice(0,2)}</label>
              <input
                type="number"
                placeholder={tt('От')}
                value={filters.balance_min}
                onChange={(e) => setFilters({...filters, balance_min: e.target.value})}
                className="form-input h-8 text-sm"
                data-testid="filter-balance-min"
              />
            </div>

            {/* Balance Max Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('cl_balance')} {t('cl_today').slice(0,2)}</label>
              <input
                type="number"
                placeholder={tt('До')}
                value={filters.balance_max}
                onChange={(e) => setFilters({...filters, balance_max: e.target.value})}
                className="form-input h-8 text-sm"
                data-testid="filter-balance-max"
              />
            </div>

            {/* Created Date From Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('cc_date')} from</label>
              <input
                type="date"
                placeholder={tt('От')}
                value={filters.created_date_from}
                onChange={(e) => setFilters({...filters, created_date_from: e.target.value})}
                className="form-input h-8 text-sm"
                data-testid="filter-created-from"
              />
            </div>

            {/* Created Date To Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('cc_date')} to</label>
              <input
                type="date"
                placeholder={tt('До')}
                value={filters.created_date_to}
                onChange={(e) => setFilters({...filters, created_date_to: e.target.value})}
                className="form-input h-8 text-sm"
                data-testid="filter-created-to"
              />
            </div>

            {/* Clear Filters */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('filter')}</label>
              <button
                onClick={clearFilters}
                className="btn btn-secondary w-full h-[32px] text-sm"
                data-testid="clear-filters"
              >
                <Filter className="w-3 h-3 mr-1" />
                {tt('Очистить')}
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions Panel */}
        {(() => {
          const shouldShow = (user?.role === 'superadmin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && selectedClients.size > 0;
          console.log('🎯 ClientsPage: Bulk Actions Panel visibility check:', {
            userRole: user?.role,
            selectedClientsSize: selectedClients.size,
            shouldShow: shouldShow
          });
          return shouldShow;
        })() && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-blue-700 font-medium">
                  Выбрано клиентов: {selectedClients.size}
                </span>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowBulkAssignModal(true)}
                  className="btn btn-primary"
                  data-testid="bulk-assign-manager-btn"
                >
                  {tt('Назначить менеджера')}
                </button>
                {user?.role === 'superadmin' && (
                  <button
                    onClick={handleBulkDeleteComments}
                    className="btn btn-danger"
                    style={{ backgroundColor: '#dc2626', color: 'white' }}
                    data-testid="bulk-delete-comments-btn"
                  >
                    {tt('Удалить все комментарии')}
                  </button>
                )}
                <button
                  onClick={() => setSelectedClients(new Set())}
                  className="btn btn-secondary"
                  data-testid="clear-selection-btn"
                >
                  {t('va_clear_filters')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Clients Table - Desktop Only */}
        <div className="content-card overflow-hidden hidden lg:block md:block" data-testid="clients-table">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="spinner mr-3"></div>
              <span className="text-gray-600">{t('loading')}</span>
            </div>
          ) : (
            <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="table compact-table" style={{ minWidth: '1500px' }} data-testid="clients-table">
                <thead>
                  <tr className="bg-gray-50">
                    {(user?.role === 'superadmin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && (
                      <th className="w-12">
                        <input
                          type="checkbox"
                          checked={paginatedClients.length > 0 && paginatedClients.every(client => selectedClients.has(client.id))}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          style={{ minWidth: '16px', minHeight: '16px', position: 'relative', zIndex: 10 }}
                          data-testid="select-all-clients"
                        />
                      </th>
                    )}
                    <th className="w-8 px-2 py-1 text-xs">
                      <button 
                        onClick={() => handleSort('name')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded text-xs font-medium"
                      >
                        <span>{t('cl_name')}</span>
                        {sortField === 'name' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="w-6 px-2 py-1 text-xs">
                      <button 
                        onClick={() => handleSort('email')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded text-xs font-medium"
                      >
                        <span>Email</span>
                        {sortField === 'email' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="w-4 px-2 py-1 text-xs">
                      <button 
                        onClick={() => handleSort('phone')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded text-xs font-medium"
                      >
                        <span>{t('cl_phone')}</span>
                        {sortField === 'phone' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="w-3 px-2 py-1 text-xs">
                      <button 
                        onClick={() => handleSort('country')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded text-xs font-medium"
                      >
                        <span>{tt('Страна')}</span>
                        {sortField === 'country' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="w-4 px-2 py-1 text-xs">
                      <button 
                        onClick={() => handleSort('balance')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded text-xs font-medium"
                      >
                        <span>{t('cl_balance')}</span>
                        {sortField === 'balance' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="w-2 px-2 py-1 text-xs">
                      <button 
                        onClick={() => handleSort('ftd')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded text-xs font-medium"
                      >
                        <span>{t('cl_ftd')}</span>
                        {sortField === 'ftd' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="w-3 px-2 py-1 text-xs">
                      <button 
                        onClick={() => handleSort('status')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded text-xs font-medium"
                      >
                        <span>{tt('Статус')}</span>
                        {sortField === 'status' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="w-3 px-2 py-1 text-xs">
                      <button 
                        onClick={() => handleSort('campaign')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded text-xs font-medium"
                      >
                        <span>{t('cl_campaign')}</span>
                        {sortField === 'campaign' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="w-3 px-2 py-1 text-xs">
                      <button 
                        onClick={() => handleSort('affiliate_id')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded text-xs font-medium"
                      >
                        <span>{t('int_api_tokens')}</span>
                        {sortField === 'affiliate_id' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    {(user?.role === 'superadmin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && (
                      <th className="w-24">
                        <button 
                          onClick={() => handleSort('assigned_manager')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('Менеджер')}</span>
                          {sortField === 'assigned_manager' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                    )}
                    <th className="w-32 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      <button 
                        onClick={() => handleSort('last_comment_date')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                      >
                        <span>{t('cl_last_comment')}</span>
                        {sortField === 'last_comment_date' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    {/* Created At Column */}
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      <button 
                        onClick={() => handleSort('created_at')} 
                        className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                      >
                        <span>{t('cl_created')}</span>
                        {sortField === 'created_at' && (
                          <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClients.length === 0 ? (
                    <tr>
                      <td colSpan={(user?.role === 'superadmin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') ? 12 : 11} className="text-center py-12 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>{t('cl_no_clients')}</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedClients
                      .filter(client => client !== null && client !== undefined)
                      .map((client) => {
                      const managerName = client.assigned_manager_name || getManagerName(client.assigned_manager) || '-';
                      
                      const handleRowClick = (e) => {
                        // Проверяем что клик НЕ на чекбоксе и НЕ на ссылке
                        const isCheckbox = e.target.type === 'checkbox' || e.target.closest('input[type="checkbox"]');
                        const isLink = e.target.tagName === 'A' || e.target.closest('a');
                        
                        if (!isCheckbox && !isLink) {
                          navigate(`/crm/clients/${client.id}`);
                        }
                      };
                      
                      return (
                        <tr 
                          key={client.id} 
                          className={`hover:bg-gray-50 cursor-pointer ${unprocessedLeadIds.has(client.id) ? 'bg-red-50 border-l-4 border-l-red-400' : ''}`}
                          onClick={handleRowClick}
                          data-testid={unprocessedLeadIds.has(client.id) ? `unprocessed-lead-row-${client.id}` : `client-row-${client.id}`}
                        >
                          {(user?.role === 'superadmin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && (
                            <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedClients.has(client.id)}
                                onChange={() => handleSelectClient(client.id)}
                                className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                data-testid={`select-client-${client.id}`}
                                style={{ minWidth: '16px', minHeight: '16px', position: 'relative', zIndex: 1 }}
                              />
                            </td>
                          )}
                          <td className="px-2 py-1 text-xs">
                            <div>
                              <Link 
                                to={`/crm/clients/${client.id}`}
                                className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer text-sm"
                                data-testid={`view-client-name-${client.id}`}
                              >
                                {client.name}
                              </Link>
                              <div className="text-xs text-gray-500">ID: {client.id}</div>
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className="text-xs text-gray-600 truncate max-w-28" title={client.email}>
                              {client.email}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className="text-xs text-gray-600">
                              {client.phone}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className="text-xs text-gray-600">
                              {client.country}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <div className="text-xs font-medium text-green-600">
                              {formatCurrency(client.total_deposits || 0, true)}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            <span className={`px-1 py-0 rounded text-xs ${
                              client.ftd ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {client.ftd ? 'Да' : 'Нет'}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-xs">{getStatusBadge(client.status)}</td>
                          <td className="px-2 py-1 text-xs">
                            <span className="text-xs text-gray-600 bg-blue-50 px-1 py-0 rounded truncate max-w-20" title={client.campaign}>
                              {client.campaign || '-'}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-xs">
                            {(() => {
                              // [AFF-2026-05-13] Show ONLY real affiliate token names:
                              // 1) Direct field on new leads (`affiliate_token_name`)
                              // 2) Lookup by `affiliate_id` against the affiliates list
                              // (Campaign suffix is the CAMPAIGN, not the affiliate — do NOT parse.)
                              const direct = client.affiliate_token_name;
                              const byId = affiliates.find(a => a.id === client.affiliate_id)?.name;
                              const name = direct || byId;
                              return (
                                <span
                                  className="text-xs text-gray-700 bg-purple-50 px-1 py-0 rounded truncate max-w-24"
                                  title={name || ''}
                                  data-testid={`client-affiliate-${client.id}`}
                                >
                                  {name || '-'}
                                </span>
                              );
                            })()}
                          </td>
                          {(user?.role === 'superadmin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && (
                            <td className="px-2 py-1 text-xs">
                              <span className="text-xs text-gray-600 truncate max-w-24" title={managerName}>
                                {managerName}
                              </span>
                            </td>
                          )}
                          <td className="px-2 py-1 text-xs">
                            <div className="text-xs text-gray-600">
                              {client.last_comment_date ? formatDateTime(client.last_comment_date) : '-'}
                            </div>
                          </td>
                          {/* Created At Column */}
                          <td className="px-2 py-1 text-xs">
                            <div className="text-xs text-gray-600">
                              {client.created_at ? formatDateTime(client.created_at, {
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : '-'}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              Показано {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalClients)} из {totalClients} клиентов
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">{t('cl_per_page')}:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary disabled:opacity-50"
              >
                {tt('Назад')}
              </button>
              
              {Array.from({ length: Math.min(totalPages, 100) }, (_, i) => i + 1)
                .filter(page => {
                  return page === 1 || 
                         page === totalPages || 
                         (page >= currentPage - 2 && page <= currentPage + 2);
                })
                .map((page, index, arr) => (
                  <React.Fragment key={page}>
                    {index > 0 && arr[index - 1] !== page - 1 && (
                      <span className="text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => goToPage(page)}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))
              }
              
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn btn-secondary disabled:opacity-50"
              >
                {tt('Далее')}
              </button>
            </div>
          )}
        </div>

        {/* Mobile Table with Horizontal Scroll */}
        <div className="lg:hidden md:hidden block">
          <div className="overflow-x-auto">
            <div className="content-card min-w-max">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <div className="spinner mr-3"></div>
                  <span className="text-gray-600">{tt('Загрузка клиентов...')}</span>
                </div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      {(user?.role === 'superadmin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && (
                        <th className="text-left py-2 px-2">
                          <input
                            type="checkbox"
                            checked={paginatedClients.length > 0 && paginatedClients.every(client => selectedClients.has(client.id))}
                            onChange={handleSelectAll}
                            className="form-checkbox"
                          />
                        </th>
                      )}
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('name')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{t('va_client')}</span>
                          {sortField === 'name' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('email')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>Email</span>
                          {sortField === 'email' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('phone')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('Телефон')}</span>
                          {sortField === 'phone' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('country')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('Страна')}</span>
                          {sortField === 'country' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('balance')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('Баланс')}</span>
                          {sortField === 'balance' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('ftd')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('ФТД')}</span>
                          {sortField === 'ftd' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('status')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('Статус')}</span>
                          {sortField === 'status' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('assigned_manager')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('Менеджер')}</span>
                          {sortField === 'assigned_manager' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('campaign')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('Кампания')}</span>
                          {sortField === 'campaign' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-700 text-xs">
                        <button 
                          onClick={() => handleSort('affiliate_id')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                          data-testid="mobile-aff-header"
                        >
                          <span>{t('int_api_tokens')}</span>
                          {sortField === 'affiliate_id' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        <button 
                          onClick={() => handleSort('last_comment_date')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('Послед. действие')}</span>
                          {sortField === 'last_comment_date' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        <button 
                          onClick={() => handleSort('created_at')} 
                          className="flex items-center justify-between w-full text-left hover:bg-gray-50 p-1 rounded"
                        >
                          <span>{tt('Дата создания')}</span>
                          {sortField === 'created_at' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedClients
                      .filter(client => client !== null && client !== undefined)
                      .map((client) => (
                      <tr 
                        key={client.id} 
                        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${unprocessedLeadIds.has(client.id) ? 'bg-red-50 border-l-4 border-l-red-400' : ''}`}
                        onClick={(e) => {
                          // Не переходить если клик по checkbox или его label
                          if (e.target.type !== 'checkbox' && !e.target.closest('input[type="checkbox"]')) {
                            navigate(`/crm/clients/${client.id}`);
                          }
                        }}
                      >
                        {(user?.role === 'superadmin' || user?.role === 'team_lead_sales' || user?.role === 'team_lead_retention') && (
                          <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedClients.has(client.id)}
                              onChange={() => handleSelectClient(client.id)}
                              className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                              style={{ minWidth: '16px', minHeight: '16px', position: 'relative', zIndex: 1 }}
                            />
                          </td>
                        )}
                        <td className="py-2 px-2">
                          <div className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                            {client.name}
                          </div>
                          <p className="text-xs text-gray-500">ID: {client.id}</p>
                        </td>
                        <td className="py-2 px-2 text-sm text-gray-600">{client.email}</td>
                        <td className="py-2 px-2 text-sm text-gray-600">{client.phone}</td>
                        <td className="py-2 px-2 text-sm text-gray-600">{client.country || '-'}</td>
                        <td className="py-2 px-2">
                          <span className="font-medium text-green-600 text-sm">
                            {formatCurrency(client.total_deposits || 0, true)}
                          </span>
                        </td>
                        <td className="py-2 px-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            client.ftd ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {client.ftd ? '✓' : '-'}
                          </span>
                        </td>
                        <td className="py-2 px-2">
                          <span className={`status-badge ${getStatusColor(client.status)}`}>
                            {getStatusLabel(client.status)}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-sm text-gray-600">
                          {client.assigned_manager_name || getManagerName(client.assigned_manager) || '-'}
                        </td>
                        <td className="py-2 px-2 text-sm text-gray-600">{client.campaign || '-'}</td>
                        <td className="py-2 px-2 text-sm">
                          {(() => {
                            // [AFF-2026-05-13] Show ONLY real affiliate token names.
                            // Do NOT parse the campaign suffix (that's the CAMPAIGN, not the affiliate).
                            const direct = client.affiliate_token_name;
                            const byId = affiliates.find(a => a.id === client.affiliate_id)?.name;
                            const name = direct || byId;
                            return (
                              <span
                                className="text-xs text-gray-700 bg-purple-50 px-2 py-0.5 rounded"
                                title={name || ''}
                                data-testid={`client-affiliate-mobile-${client.id}`}
                              >
                                {name || '-'}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-2 px-2 text-sm text-gray-600">
                          {client.last_comment_date ? formatDateTime(client.last_comment_date) : '-'}
                        </td>
                        <td className="py-2 px-2 text-sm text-gray-600">
                          {formatDateTime(client.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Add Client Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="add-client-modal">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('cl_add_client')}</h3>
              
              {errors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                  {errors.general}
                </div>
              )}
              
              <form onSubmit={handleCreateClient}>
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label">{tt('ФИО')}</label>
                    <input
                      type="text"
                      value={newClient.name}
                      onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                      className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                      placeholder={tt('Иван Петров')}
                      data-testid="client-name-input"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                      className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="ivan@example.com"
                      data-testid="client-email-input"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">{tt('Телефон')}</label>
                    <input
                      type="text"
                      value={newClient.phone}
                      onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                      className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
                      placeholder="+7901234567"
                      data-testid="client-phone-input"
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">{tt('Страна')}</label>
                    <InputDropdown
                      value={newClient.country}
                      onChange={(value) => setNewClient({...newClient, country: value})}
                      options={countries}
                      placeholder={tt('Выберите или введите страну')}
                      className={errors.country ? 'border-red-500' : ''}
                      testId="client-country-input"
                      allowInput={true}
                    />
                    {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">{tt('Компейн')}</label>
                    <InputDropdown
                      value={newClient.campaign}
                      onChange={(value) => setNewClient({...newClient, campaign: value})}
                      options={campaigns}
                      placeholder={tt('Выберите или введите кампанию')}
                      className=""
                      testId="client-campaign-input"
                      allowInput={true}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{tt('Статус')}</label>
                    <InputSelect
                      value={newClient.status || 'new'}
                      onChange={(value) => setNewClient({...newClient, status: value})}
                      options={statuses}
                      placeholder={tt('Выберите статус')}
                      className=""
                      testId="client-status-select"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setNewClient({ name: '', email: '', phone: '', country: '', campaign: '', status: 'new' });
                      setErrors({});
                    }}
                    className="btn btn-secondary"
                    data-testid="cancel-add-client"
                  >
                    {tt('Отмена')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    data-testid="submit-add-client"
                  >
                    {tt('Создать')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      {/* Bulk Assign Manager Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="bulk-assign-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Назначить менеджера')}</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                {tt('Выбрано клиентов:')} <strong>{selectedClients.size}</strong>
              </p>
              
              {errors.bulk && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                  {errors.bulk}
                </div>
              )}
              
              <div className="form-group">
                <label className="form-label">{tt('Менеджер')}</label>
                <InputSelect
                  value={bulkAssignManager}
                  onChange={(value) => setBulkAssignManager(value)}
                  options={[
                    { value: '', label: tt('Не назначать менеджера') },
                    ...managers.map(manager => ({ value: manager.id, label: manager.name }))
                  ]}
                  placeholder={tt('Выберите менеджера')}
                  className=""
                  testId="bulk-manager-select"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">{tt('Статус (необязательно)')}</label>
                <InputSelect
                  value={bulkAssignStatus}
                  onChange={(value) => setBulkAssignStatus(value)}
                  options={[
                    { value: '', label: tt('Не изменять статус') },
                    ...statuses
                  ]}
                  placeholder={tt('Выберите статус')}
                  className=""
                  testId="bulk-status-select"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowBulkAssignModal(false);
                  setBulkAssignManager('');
                  setBulkAssignStatus('');
                  setErrors({});
                }}
                className="btn btn-secondary"
                data-testid="cancel-bulk-assign"
              >
                {tt('Отмена')}
              </button>
              <button
                onClick={handleBulkAssignManager}
                className="btn btn-primary"
                data-testid="submit-bulk-assign"
              >
                {tt('Назначить')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsPage;